import React, { createContext, useContext, useCallback } from 'react';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  query, 
  where
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { isWeekend } from '../utils/calendarUtils';

const GroupAvailabilityContext = createContext();

export function useGroupAvailability() {
  return useContext(GroupAvailabilityContext);
}

export function GroupAvailabilityProvider({ children }) {
  const { currentUser } = useAuth();

  // Helper function to safely get date from Firestore Timestamp or Date object
  const getDateFromData = (dateData) => {
    if (!dateData) return null;
    
    try {
      // Handle Firestore Timestamp
      if (dateData?.toDate && typeof dateData.toDate === 'function') {
        return dateData.toDate();
      }
      
      // Handle Date object
      if (dateData instanceof Date) {
        return dateData;
      }
      
      // Handle string dates
      if (typeof dateData === 'string') {
        const parsedDate = new Date(dateData);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
      }
      
      // Handle timestamp numbers
      if (typeof dateData === 'number') {
        const parsedDate = new Date(dateData);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate;
        }
      }
      
      console.warn('GroupAvailabilityContext: Could not parse date data:', dateData);
      return null;
    } catch (error) {
      console.error('GroupAvailabilityContext: Error converting date data:', dateData, error);
      return null;
    }
  };

  // Get group availability for a specific group (automatically includes all group members)
  const getGroupAvailability = useCallback(async (groupId, includeWeekdays = true, targetMonth = null) => {
    if (!currentUser || !groupId) return {};

    try {
      console.log('GroupAvailabilityContext: Fetching group availability for group:', groupId, 'includeWeekdays:', includeWeekdays, 'targetMonth:', targetMonth);
      
      // First, get the group details to find all members
      const groupDoc = await getDoc(doc(db, 'groups', groupId));
      if (!groupDoc.exists()) {
        throw new Error('Group not found');
      }
      
      const groupData = groupDoc.data();
      const memberIds = groupData.members || [];
      
      console.log('GroupAvailabilityContext: Group data:', groupData);
      console.log('GroupAvailabilityContext: Member IDs:', memberIds);
      
      if (memberIds.length === 0) {
        console.log('GroupAvailabilityContext: No members found in group');
        return {};
      }

      console.log('GroupAvailabilityContext: Group has', memberIds.length, 'members, fetching their availability');

      // Get user profiles for member names
      const userProfilesQuery = query(
        collection(db, 'users'),
        where('uid', 'in', memberIds)
      );
      
      const profilesSnapshot = await getDocs(userProfilesQuery);
      const memberProfiles = {};
      
      profilesSnapshot.forEach((doc) => {
        const profile = doc.data();
        memberProfiles[profile.uid] = profile;
      });

      // Get unavailable dates for all group members
      const unavailableDatesQuery = query(
        collection(db, 'unavailableDates'),
        where('userId', 'in', memberIds)
      );
      
      console.log('GroupAvailabilityContext: Querying unavailable dates for members:', memberIds);
      
      const datesSnapshot = await getDocs(unavailableDatesQuery);
      const memberDates = [];
      
      console.log('GroupAvailabilityContext: Found', datesSnapshot.size, 'unavailable dates for group members');
      
      datesSnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('GroupAvailabilityContext: Processing unavailable date:', {
          userId: data.userId,
          date: data.date,
          reason: data.reason
        });
        memberDates.push({
          id: doc.id,
          ...data
        });
      });

      console.log('GroupAvailabilityContext: Processed member dates:', memberDates.length);
      console.log('GroupAvailabilityContext: Sample member date:', memberDates[0]);

      // Organize availability by date
      const availabilityByDate = {};
      
      // Use targetMonth if provided, otherwise use current month
      const monthToUse = targetMonth || new Date();
      const monthStart = new Date(monthToUse.getFullYear(), monthToUse.getMonth(), 1);
      const monthEnd = new Date(monthToUse.getFullYear(), monthToUse.getMonth() + 1, 0);
      
      console.log('GroupAvailabilityContext: Generating availability for month:', monthToUse.toDateString());
      console.log('GroupAvailabilityContext: Month range:', monthStart.toDateString(), 'to', monthEnd.toDateString());
      
      // Generate all dates in the target month
      const currentDate = new Date(monthStart);
      while (currentDate <= monthEnd) {
        const dateString = currentDate.toDateString();
        const date = new Date(currentDate);
        
        // Skip weekdays if includeWeekdays is false
        if (!includeWeekdays && !isWeekend(date)) {
          currentDate.setDate(currentDate.getDate() + 1);
          continue;
        }
        
        // Find members unavailable on this date
        const unavailableMembers = memberDates
          .filter(dateData => {
            if (!dateData.date) return false;
            const dateFromData = getDateFromData(dateData.date);
            if (!dateFromData) return false;
            
            // Compare dates by converting to date strings for accurate comparison
            const dateDataString = dateFromData.toDateString();
            const currentDateString = dateString;
            
            return dateDataString === currentDateString;
          })
          .map(dateData => ({
            userId: dateData.userId,
            name: memberProfiles[dateData.userId]?.displayName || 'Unknown User',
            reason: dateData.reason || ''
          }));
        
        // Find available members (those not in unavailable list)
        const availableMembers = memberIds
          .filter(memberId => !unavailableMembers.some(un => un.userId === memberId))
          .map(memberId => ({
            userId: memberId,
            name: memberProfiles[memberId]?.displayName || 'Unknown User'
          }));
        
        const hasConflicts = unavailableMembers.length > 0;
        
        // Debug logging for dates with conflicts
        if (hasConflicts) {
          console.log('GroupAvailabilityContext: Date has conflicts:', dateString, {
            unavailableMembers: unavailableMembers.length,
            availableMembers: availableMembers.length,
            totalMembers: memberIds.length,
            hasConflicts: hasConflicts
          });
        }
        
        // Always create availability data for every date, even if no conflicts
        availabilityByDate[dateString] = {
          date: date,
          unavailableMembers,
          availableMembers,
          totalMembers: memberIds.length,
          hasConflicts: hasConflicts,
          note: 'All group members automatically share availability'
        };
        
        // Move to next date
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      console.log('GroupAvailabilityContext: Generated availability for', Object.keys(availabilityByDate).length, 'dates');
      console.log('GroupAvailabilityContext: Sample availability data:', Object.entries(availabilityByDate).slice(0, 3));
      
      console.log('GroupAvailabilityContext: Group availability calculated:', availabilityByDate);
      
      // Log summary of conflicts found
      const datesWithConflicts = Object.entries(availabilityByDate).filter(([_, data]) => data.hasConflicts);
      console.log('GroupAvailabilityContext: Found', datesWithConflicts.length, 'dates with conflicts:', 
        datesWithConflicts.map(([date, data]) => ({
          date,
          unavailableMembers: data.unavailableMembers.length,
          availableMembers: data.availableMembers.length
        }))
      );
      
      return availabilityByDate;
      
    } catch (error) {
      console.error('GroupAvailabilityContext: Error fetching group availability:', error);
      return {};
    }
  }, [currentUser]);

  // Get availability stats for a group
  const getGroupAvailabilityStats = useCallback(async (groupId, targetMonth = null) => {
    if (!currentUser || !groupId) return {};

    try {
      const availability = await getGroupAvailability(groupId, true, targetMonth);
      
      if (!availability || Object.keys(availability).length === 0) {
        return {
          totalDates: 0,
          conflictDates: 0,
          averageAvailability: 0,
          totalMembers: 0
        };
      }

      const dates = Object.values(availability);
      const totalDates = dates.length;
      const conflictDates = dates.filter(d => d.hasConflicts).length;
      const totalMembers = dates[0]?.totalMembers || 0;
      
      // Calculate average availability percentage
      const totalAvailability = dates.reduce((sum, date) => {
        return sum + (date.availableMembers.length / date.totalMembers);
      }, 0);
      const averageAvailability = totalDates > 0 ? (totalAvailability / totalDates) * 100 : 0;

      return {
        totalDates,
        conflictDates,
        averageAvailability: Math.round(averageAvailability),
        totalMembers
      };
    } catch (error) {
      console.error('GroupAvailabilityContext: Error getting availability stats:', error);
      return {};
    }
  }, [currentUser, getGroupAvailability]);

  const value = {
    getGroupAvailability,
    getGroupAvailabilityStats
  };

  return (
    <GroupAvailabilityContext.Provider value={value}>
      {children}
    </GroupAvailabilityContext.Provider>
  );
}
